import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Users } from "lucide-react";

import { patientMergeApi, type DuplicateCandidate, type DuplicateSide } from "../../shared/api/patients";
import { apiError } from "../../shared/api/client";
import { formatDate } from "../../shared/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * T93 / #18862 — duplicate review and merge.
 *
 * Detection already ran; this is the half a person performs. Two records side by side, and one
 * decision: not a duplicate, or this one is the master.
 *
 * The confirm step is deliberate rather than decorative — merge is irreversible in Phase 1, so the
 * dialog names which record survives and which one stops being editable.
 */

const CONFIDENCE_LABEL: Record<DuplicateCandidate["confidence"], string> = {
  STRONG: "Rất giống",
  LIKELY: "Có thể trùng",
  WEAK: "Ít khả năng",
};

const FIELDS: { label: string; get: (s: DuplicateSide) => string | null }[] = [
  { label: "Mã BN", get: (s) => s.patientCode },
  { label: "Họ tên", get: (s) => s.fullName },
  { label: "CCCD", get: (s) => s.nationalId },
  { label: "BHYT", get: (s) => s.insuranceNo },
  { label: "Điện thoại", get: (s) => s.phone },
  { label: "Ngày sinh", get: (s) => (s.dateOfBirth ? formatDate(s.dateOfBirth) : null) },
  { label: "Giới tính", get: (s) => s.gender },
  { label: "Địa chỉ", get: (s) => s.address },
];

export function PatientMergePage() {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState<{
    candidate: DuplicateCandidate;
    master: DuplicateSide;
    source: DuplicateSide;
  } | null>(null);

  const q = useQuery({
    queryKey: ["patient-duplicates"],
    queryFn: () => patientMergeApi.listCandidates(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["patient-duplicates"] });
    qc.invalidateQueries({ queryKey: ["patients"] });
  };

  const dismiss = useMutation({
    mutationFn: (id: string) => patientMergeApi.dismiss(id),
    onSuccess: invalidate,
  });

  const merge = useMutation({
    mutationFn: (v: { sourceId: string; targetId: string }) =>
      patientMergeApi.merge(v.sourceId, v.targetId),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  const rows = q.data?.content ?? [];

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="mb-1 flex items-center gap-2 text-xl font-semibold">
          <Users className="size-5" />
          Hồ sơ trùng
        </h1>
        <p className="m-0 text-sm text-muted-foreground">
          Hệ thống phát hiện các cặp hồ sơ có thể là cùng một người. Chọn hồ sơ chính để gộp, hoặc
          đánh dấu không trùng. <strong>Gộp không thể hoàn tác.</strong>
        </p>
      </header>

      {q.isLoading && <Skeleton className="h-40 w-full" />}
      {q.isError && (
        <p className="text-sm text-destructive">
          Không tải được danh sách. {apiError(q.error).detail}
        </p>
      )}

      {!q.isLoading && rows.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Không có cặp hồ sơ nào chờ xử lý.
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {rows.map((c) => {
          const a = c.primary;
          const b = c.candidate;
          if (!a || !b) return null;
          return (
            <Card key={c.id}>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-base">
                  {a.fullName} · {b.fullName}
                </CardTitle>
                <Badge variant={c.confidence === "STRONG" ? "destructive" : "warning"}>
                  {CONFIDENCE_LABEL[c.confidence]} ({Math.round(Number(c.score) * 100)}%)
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {FIELDS.map((f) => {
                        const va = f.get(a);
                        const vb = f.get(b);
                        // Differences are what the reviewer is looking for; matches are why the
                        // pair was raised. Highlighting the difference is the useful half.
                        const differs = (va ?? "") !== (vb ?? "");
                        return (
                          <tr key={f.label} className="border-b last:border-0">
                            <td className="w-32 py-1.5 text-muted-foreground">{f.label}</td>
                            <td className={`py-1.5 ${differs ? "font-medium" : ""}`}>
                              {va ?? "—"}
                            </td>
                            <td className={`py-1.5 ${differs ? "font-medium" : ""}`}>
                              {vb ?? "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={dismiss.isPending}
                    onClick={() => dismiss.mutate(c.id)}
                  >
                    Không trùng
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setConfirm({ candidate: c, master: a, source: b })}
                  >
                    Chọn {a.fullName} làm hồ sơ chính
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setConfirm({ candidate: c, master: b, source: a })}
                  >
                    Chọn {b.fullName} làm hồ sơ chính
                  </Button>
                </div>
                {dismiss.isError && (
                  <p className="mb-0 mt-2 text-sm text-destructive">
                    {apiError(dismiss.error).detail}
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="size-5" />
              Xác nhận gộp hồ sơ
            </DialogTitle>
            <DialogDescription>
              Thao tác này <strong>không thể hoàn tác</strong>.
            </DialogDescription>
          </DialogHeader>

          {confirm && (
            <ul className="space-y-2 text-sm">
              <li className="rounded border px-3 py-2">
                <span className="text-muted-foreground">Hồ sơ chính (giữ lại): </span>
                <span className="font-semibold">{confirm.master.fullName}</span>{" "}
                <span className="text-muted-foreground">
                  {confirm.master.patientCode ?? confirm.master.id.slice(0, 8)}
                </span>
              </li>
              <li className="rounded border border-destructive/40 px-3 py-2">
                <span className="text-muted-foreground">Hồ sơ được gộp vào: </span>
                <span className="font-semibold">{confirm.source.fullName}</span>{" "}
                <span className="text-muted-foreground">
                  {confirm.source.patientCode ?? confirm.source.id.slice(0, 8)}
                </span>
                <div className="mt-1 text-xs text-muted-foreground">
                  Hồ sơ này sẽ không sửa được nữa. Lịch sử khám, đơn thuốc và hoá đơn của nó vẫn
                  xem được từ hồ sơ chính.
                </div>
              </li>
            </ul>
          )}

          {merge.isError && (
            <p className="mb-0 text-sm text-destructive">{apiError(merge.error).detail}</p>
          )}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button type="button" variant="outline" onClick={() => setConfirm(null)}>
              Huỷ
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={merge.isPending}
              onClick={() =>
                confirm &&
                merge.mutate({ sourceId: confirm.source.id, targetId: confirm.master.id })
              }
            >
              Gộp hồ sơ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
